/**
 * Supplies & Used Supplies — pure logic (PRD.264).
 *
 * Dependency-free helpers for supply-kind defaults, supply validation,
 * sellability, used-supply billing status, quantity/batch validation, and
 * reversal guards. Import-free so they can be unit-tested with Node's built-in
 * runner and reused by the inventory form, invoice picker, and used-supply
 * service.
 */

export type SupplyKind = 'consumable' | 'expendable';
export type UsedSupplyBillingStatus = 'not_billable' | 'ready_for_billing' | 'billed';

/** Sellability default by supply kind (PRD.264 §3): consumable sells, expendable doesn't. */
export function defaultIsSellableForKind(kind: SupplyKind): boolean {
  return kind === 'consumable';
}

/**
 * Whether an inventory item is sellable (invoice/billing picker visibility).
 * `is_sellable` is stored as 0/1; null (legacy rows before this column) is
 * treated as sellable so existing medicine/product/package stay visible.
 */
export function isSellable(isSellableFlag: number | null | undefined): boolean {
  if (isSellableFlag === null || isSellableFlag === undefined) return true;
  return isSellableFlag === 1;
}

/**
 * Validate the supply kind on an inventory item (PRD.264 §2, §3). Required and
 * constrained to consumable/expendable for supplies; ignored otherwise.
 */
export function validateSupplyItemType(input: {
  itemType: string;
  supplyUsageType: string | null;
}): string | null {
  if (input.itemType !== 'supply') return null;
  if (input.supplyUsageType !== 'consumable' && input.supplyUsageType !== 'expendable') {
    return 'Supply Kind is required (Consumable or Expendable).';
  }
  return null;
}

/** Initial billing status for a used supply (PRD.264 §14). */
export function initialUsedSupplyBillingStatus(sellable: boolean): UsedSupplyBillingStatus {
  return sellable ? 'ready_for_billing' : 'not_billable';
}

/**
 * Validate a used-supply record before posting (PRD.264 §13). Quantity must be
 * > 0; a batch is required when the supply is batch-tracked; usage may not
 * exceed available batch stock unless the clinic allows negative stock.
 */
export function validateUsedSupply(input: {
  quantity: number;
  batchTracked: boolean;
  batchId: string | null;
  available?: number | null;
  allowNegative?: boolean;
}): string | null {
  if (!Number.isFinite(input.quantity) || input.quantity <= 0) {
    return 'Quantity must be greater than 0.';
  }
  if (input.batchTracked && !input.batchId) {
    return 'Select a batch before recording this supply.';
  }
  if (
    !input.allowNegative &&
    input.available !== null &&
    input.available !== undefined &&
    input.quantity > input.available
  ) {
    return 'Not enough available stock in the selected batch.';
  }
  return null;
}

/** Reversal guard for a used supply (PRD.264 §18). */
export function canReverseUsedSupply(input: {
  usageStatus: string | null;
  billingStatus: string | null;
  deletedAt: string | null;
}): { ok: boolean; error?: string } {
  if (input.deletedAt) {
    return { ok: false, error: 'This usage record is no longer available.' };
  }
  if (input.usageStatus === 'reversed') {
    return { ok: false, error: 'This supply usage has already been reversed.' };
  }
  if (input.billingStatus === 'billed') {
    return {
      ok: false,
      error:
        'This supply has already been added to billing. Remove or reverse the invoice line before reversing the supply usage.',
    };
  }
  return { ok: true };
}

/** Batch availability after deducting a usage quantity (stock movement math). */
export function availableAfterUsage(available: number, quantity: number): number {
  return available - quantity;
}
